// TransferOptions.jsx
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from 'styled-components';
import { centsToDollars } from 'shared-components/src/utils/currency';
import HtmlRenderer from 'shared-components/src/components/HtmlRenderer';
import {
  Container,
  BalanceCard,
  BalanceContent,
  BalanceHeader,
  BalanceValue,
  SectionTitle,
  SectionSubtitle,
  OptionsList,
  OptionCard,
  OptionRow,
  IconBubble,
  OptionBody,
  OptionTitleRow,
  Description,
  Fee,
  RadioWrap,
  ContinueWrap,
  DynamicText,
} from './styles';
import { PrimaryButton, ProgressBar, ProgressFill } from '../style';
import InteracImg from '../images/Interac-transfer.png';
import MastercardImg from '../images/master_card.png';
import VisaDirectImg from '../images/visa_direct_logo.jpeg';
import { DisbursementsContext } from '../../../contexts/DisbursementsContext';
import { getDisbursements, getTransferOptions } from '../services';
import { Spinner } from '../InteracTransfer/styles';
import { formatIntlCurrency } from '../../../utils/helpers';
import { MoneyPlusIcon } from '../../../components/Icons';

/* ========= TransferOptions (with custom guards) ========= */

export default function TransferOptions() {
  const navigate = useNavigate();
  const intl = useIntl();
  const theme = useContext(ThemeContext);
  const {
    redeemToken,
    disbursementData,
    setdisbursementData,
    transferOptions,
    setTransferOptions,
    clearSession,
  } = useContext(DisbursementsContext);

  const [selectedOption, setSelectedOption] = useState('');
  const { amount = 0 } = disbursementData || {};
  const [isLoading, setisLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);

  const transferList = useMemo(
    () => [
      {
        id: 'etransfer',
        img: InteracImg,
        title: intl.messages['transfer.interac.title'],
        description: intl.messages['transfer.interac.desc'],
      },
      {
        id: 'visa_direct',
        img: VisaDirectImg,
        title: intl.messages['transfer.visa.title'],
        description: intl.messages['transfer.visa.desc'],
      },
      {
        id: 'keep_card',
        img: MastercardImg,
        title: intl.messages['transfer.keep.title'],
        description: intl.messages['transfer.keep.desc'],
      },
    ],
    [intl]
  );

  const fetchDisbursementData = async () => {
    try {
      const response = await getDisbursements(redeemToken);
      setdisbursementData(response.data);
    } catch (error) {
      setError(error);
    }
  };
  const fetchTransferOptions = async redeemToken => {
    setisLoading(true);
    try {
      const response = await getTransferOptions(redeemToken);
      setTransferOptions(
        response.data.map(option => ({
          id: option.type,
          type: option.type,
          img: transferList.find(item => item.id === option.type).img || '',
          title: transferList.find(item => item.id === option.type).title || '',
          description:
            transferList.find(item => item.id === option.type).description ||
            '',
          fee: option.fee || null,
        })) || []
      );
    } catch (error) {
      setError(error);
    } finally {
      setisLoading(false);
    }
  };

  useEffect(() => {
    if (!redeemToken) {
      navigate('/disbursements/verification', { replace: true });
    } else {
      fetchDisbursementData();
      fetchTransferOptions(redeemToken);
    }
  }, [redeemToken, navigate]);

  // Track if we should allow navigation (when user explicitly clicks continue)
  const shouldBlockRef = React.useRef(true);

  const gotoTerminated = type => {
    const message = `
⚠️ ${intl.messages['session.warning.title']}

${intl.messages[`session.warning.instruction.${type}`]}
        `;
    // native confirm dialog is blocking/synchronous
    // eslint-disable-next-line no-alert
    if (window.confirm(message.trim())) {
      // Unblock navigation and proceed to termination page
      shouldBlockRef.current = false;
      // clear any session state, then hard-redirect (or navigate with replace)
      sessionStorage.removeItem('x-guard-flow');
      clearSession();
      navigate(`/disbursements/session-terminated?type=${type}`, { replace: true });
    } else if (type === 'refresh') {
      shouldBlockRef.current = false;
      navigate(`/disbursements/session-terminated?type=${type}`, { replace: true });
    }
  };

  // Block browser back/forward navigation
  useEffect(() => {
    // Push a dummy state to prevent going back
    window.history.pushState(null, '', window.location.pathname);

    const handlePopState = () => {
      if (shouldBlockRef.current) {
        // Push the state again to keep user on the page
        window.history.pushState(null, '', window.location.pathname);
        gotoTerminated('back');
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    const flowActive = sessionStorage.getItem('x-guard-flow') === 'active';
    if (!flowActive) {
      sessionStorage.setItem('x-guard-flow', 'active');
    }

    // Check if page was reloaded using modern API
    const navEntries = performance.getEntriesByType('navigation');
    const isReload = navEntries.length > 0 && navEntries[0].type === 'reload';

    if (flowActive && isReload) {
      // We just reloaded – show your custom refresh modal
      gotoTerminated('refresh');
    }

    // Also catch BFCache (back/forward cache) restores (Safari/iOS)
    const onPageShow = e => {
      if (e.persisted && flowActive) {
        // Coming back from BFCache; treat like a navigation violation
        gotoTerminated('back');
      }
    };
    window.addEventListener('pageshow', onPageShow);

    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  useEffect(() => {
    // Catch keyboard refresh & show custom modal (toolbar refresh cannot be caught)
    const onKeyDown = e => {
      const isCtrlR =
        (e.key === 'r' || e.key === 'R') && (e.ctrlKey || e.metaKey);
      const isF5 = e.key === 'F5';
      if (isCtrlR || isF5) {
        e.preventDefault();
        gotoTerminated('refresh');
      }
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      // clean up
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const goto = path => {
    // Unblock navigation before proceeding
    shouldBlockRef.current = false;
    // clear any session state, then navigate
    sessionStorage.removeItem('x-guard-flow');
    navigate(path);
  };

  const handleContinue = () => {
    if (!selectedOption) return;
    if (selectedOption === 'etransfer') goto('/disbursements/interac-transfer');
    else if (selectedOption === 'visa_direct')
      goto('/disbursements/visa-direct');
    else if (selectedOption === 'keep_card')
      goto('/disbursements/create-account');
  };

  const renderDescription = description =>
    description.split('\n').map((line, i) => {
      const uniqId = `line-${i}`;
      const linkMatch = line.match(/<a\s+href="([^"]*)"[^>]*>([^<]*)<\/a>/);
      if (linkMatch) {
        const [, href, linkText] = linkMatch;
        return (
          <div className="row" key={uniqId}>
            <span className="dot">•</span>
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline"
              onClick={e => e.stopPropagation()}
            >
              {linkText}
            </a>
            <span>{line.replace(linkMatch[0], '')}</span>
          </div>
        );
      }

      // Handle <b> tags with content
      const boldMatch = line.match(/<b>([^<]+)<\/b>/);
      if (boldMatch) {
        const [fullMatch, boldText] = boldMatch;
        const parts = line.split(fullMatch);
        return (
          <div className="row" key={uniqId}>
            <span className="dot">•</span>
            <span>
              {parts[0]}
              <b>{boldText}</b>
              {parts[1]}
            </span>
          </div>
        );
      }

      // Handle <sup><small>MD</small></sup>
      const supMatch = line.match(/<sup><small>([^<]+)<\/small><\/sup>/);
      if (supMatch) {
        const [fullMatch, supText] = supMatch;
        const parts = line.split(fullMatch);
        return (
          <div className="row" key={uniqId}>
            <span className="dot">•</span>
            <span>
              {parts[0]}
              <sup>
                <small>{supText}</small>
              </sup>
              {parts[1]}
            </span>
          </div>
        );
      }

      return (
        <div className="row" key={uniqId}>
          <span className="dot">•</span>
          <span>{line}</span>
        </div>
      );
    });

  const renderLink = htmlString => {
    const linkMatch = htmlString.match(/<a\s+href="([^"]*)"[^>]*>([^<]*)<\/a>/);
    if (linkMatch) {
      const [, href, linkText] = linkMatch;
      const cardholderPdf = intl.locale === 'fr' ? theme.brand.activateCardholderAgreementFr : theme.brand.activateCardholderAgreementEn;
      const finalHref = href === '/cardholder-agreement.pdf' ? cardholderPdf : href;
      return (
        <a
          href={finalHref}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline"
        >
          {linkText}
        </a>
      );
    }
    return null;
  };
  return (
    <>
      <ProgressBar>
        <ProgressFill className="third" />
      </ProgressBar>

      {isLoading ? (
        <Container
          style={{
            paddingTop: '2rem',
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Spinner style={{ width: '2rem', height: '2rem' }} />
        </Container>
      ) : (
        <Container>
          {/* Balance */}
          <BalanceCard>
            <BalanceContent>
              <BalanceHeader>
                <MoneyPlusIcon />
                <h2>{intl.messages['transfer.balance.title']}</h2>
              </BalanceHeader>
              <div>
                <BalanceValue>
                  {formatIntlCurrency(centsToDollars(amount || 0), intl.locale)}
                </BalanceValue>
              </div>
            </BalanceContent>
          </BalanceCard>

          {/* Options */}
          <SectionTitle>{intl.messages['transfer.section.title']}</SectionTitle>
          <SectionSubtitle>
            {intl.messages['transfer.section.subtitle']}
          </SectionSubtitle>

          <OptionsList>
            {(transferOptions || []).map(option => (
              <OptionCard
                key={option.id}
                $selected={selectedOption === option.id}
                onClick={() => setSelectedOption(option.id)}
              >
                <OptionRow>
                  <IconBubble>
                    <img src={option.img} alt={option.id} className="size-mc" />
                  </IconBubble>

                  <OptionBody>
                    <OptionTitleRow>
                      <h3><HtmlRenderer html={option.title} /></h3>
                    </OptionTitleRow>

                    <Description>
                      {renderDescription(option.description)}
                    </Description>

                    {option.fee && (
                      <Fee>
                        {intl.messages['transfer.fee']}{' '}
                        {formatIntlCurrency(option.fee, intl.locale)}
                      </Fee>
                    )}
                    {option.type === 'keep_card' && (
                      <Fee>
                        {renderLink(
                          intl.messages['transfer.keep.cardholder.link']
                        )}{' '}
                        {intl.messages['transfer.keep.cardholder'] || ''}
                      </Fee>
                    )}
                  </OptionBody>

                  <RadioWrap>
                    <input
                      type="radio"
                      id={option.id}
                      name="transfer-option"
                      value={option.id}
                      checked={selectedOption === option.id}
                      onChange={() => setSelectedOption(option.id)}
                    />
                  </RadioWrap>
                </OptionRow>
              </OptionCard>
            ))}
          </OptionsList>

          {/* Trademark Disclaimer */}
          <div style={{ margin: '1rem 0', fontSize: '0.875rem', color: '#555' }}>
            <p><HtmlRenderer html={intl.messages['transfer.trademark.disclaimer.interac']} /></p>
            <p><HtmlRenderer html={intl.messages['transfer.trademark.disclaimer.visa']} /></p>
            <p><HtmlRenderer html={intl.messages['transfer.trademark.disclaimer.mastercard']} /></p>
          </div>

          {/* Continue */}
          <ContinueWrap>
            <PrimaryButton onClick={handleContinue} disabled={!selectedOption}>
              {intl.messages['transfer.continue']}
            </PrimaryButton>
          </ContinueWrap>

          {!selectedOption && (
            <DynamicText>{intl.messages['transfer.select']}</DynamicText>
          )}
        </Container>
      )}
    </>
  );
}

